/*
 * @providesModule HighlightArrow
 *
 */

import React from 'react';

import _ from 'lodash';
import * as Animatable from 'react-native-animatable';

import Sizes from 'Sizes';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import SKZConstants from 'SKZConstants';

import {Animated, Easing, LayoutChangeEvent, Platform, StyleSheet, View} from 'react-native';

const TAB_BAR_RELATIVE_SIZE = 0.28;
const MILLISECONDS_PER_SECOND = SKZConstants.MS_PER_SEC;

type RefLayout = {
  width: number;
  height: number;
  rootX: number;
  rootY: number;
};
type HighlightArrowProps = {
  direction: 'top' | 'bottom' | 'left' | 'right';
  offset: {x: number; y: number};
  shouldAdjustForHome?: boolean;
  size?: {width: number; height: number};
  refLayout?: RefLayout;
  shouldBounce?: boolean;
  imageURL?: string;
  shouldRenderImage?: boolean;
  imageColor?: string;
  shouldNotTheme?: boolean;
  onLayout?: () => void;
  testID?: string;
  isMatchSelect?: boolean;
  ignoreLayout?: boolean;
};

type HighlightArrowState = {
  arrowSize: {width: number; height: number};
  refLayout: RefLayout;
  size: {width: number; height: number};
  opacity: number;
  bounceOffset: Animated.ValueXY;
  imageURL: string;
  imageColor: string;
  shouldRender: boolean;
  bouncing?: Animated.CompositeAnimation;
};

export default class HighlightArrow extends React.Component<
  HighlightArrowProps,
  HighlightArrowState
> {
  shouldRenderImage: boolean;
  unmounted: any;

  constructor(props: any) {
    super(props);
    const size = props.size
      ? props.size
      : Sizes.dynamic({
          sm: {width: 44, height: 30},
          xxlg: {width: 44, height: 30},
        });
    const image = props.imageURL ? props.imageURL : 'ic_pointer_skz.png';
    const imageTheme = props.imageColor ? props.imageColor : 'transparent';

    this.shouldRenderImage =
      this.props.shouldRenderImage == undefined || this.props.shouldRenderImage;

    this.state = {
      arrowSize: size,
      refLayout: props.refLayout,
      size: size,
      opacity: 0,
      bounceOffset: new Animated.ValueXY({x: 0, y: 0}),
      imageURL: image,
      imageColor: imageTheme,
      shouldRender: false,
    };

    setTimeout(() => {
      this.setState({
        shouldRender: true,
      });
    }, SKZConstants.MS_PER_HALF_SEC);
    this._updateOpacity(this.props.refLayout);
  }

  static getDerivedStateFromProps(
    props: HighlightArrowProps,
    state: HighlightArrowState
  ): Object | null {
    if (props.refLayout !== state.refLayout) {
      return {
        refLayout: props.refLayout,
        opacity: 0,
      };
    }
    return null;
  }

  componentDidUpdate(prevProps: HighlightArrowProps) {
    if (prevProps.refLayout !== this.props.refLayout) {
      this._updateOpacity(this.props.refLayout);
    }
  }

  componentDidMount() {
    if (this._shouldBounce()) {
      this._startBounce();
    }
    _.invoke(this.props, 'onLayout');
  }

  componentWillUnmount() {
    this._stopBouncing();
  }

  _updateOpacity(layout: RefLayout | undefined) {
    if (!this._shouldHide(layout)) {
      setTimeout(() => {
        this.setState({
          opacity: 1.0,
        });
      }, 300);
    } else {
      this.setState({
        opacity: 0,
      });
    }
  }

  _getPositionForLayout(
    width: number,
    height: number,
    rootX: number,
    rootY: number
  ): {left: number; top: number} {
    const halfX = width / 2 + rootX;
    const halfY = height / 2 + rootY;

    let posX = 0;
    let posY = 0;
    if (this.props.direction == 'top') {
      posX = halfX + this.props.offset.x - this.state.size.width / 2;
      posY = rootY - this.props.offset.y - this.state.size.height;
    } else if (this.props.direction == 'bottom') {
      posX = halfX + this.props.offset.x - this.state.size.width / 2;
      posY = rootY + height - this.props.offset.y;
    } else if (this.props.direction == 'left') {
      posX = rootX + this.props.offset.x - this.state.size.width;
      posY = halfY - this.props.offset.y - this.state.size.height / 2;
    } else {
      posX = rootX + width + this.props.offset.x;
      posY = halfY - this.props.offset.y - this.state.size.height / 2;
    }

    return {
      left: posX,
      top: posY,
    };
  }

  _getRotationForDirection(): Object {
    if (this.props.direction == 'top') {
      return {};
    } else if (this.props.direction == 'bottom') {
      return {transform: [{rotate: '180deg'}]};
    } else if (this.props.direction == 'left') {
      return {transform: [{rotate: '270deg'}]};
    }
    return {transform: [{rotate: '90deg'}]};
  }

  _getAnimationForDirection() {
    if (this.props.direction == 'top') {
      return 'fadeInDown';
    } else if (this.props.direction == 'bottom') {
      return 'fadeInUp';
    } else if (this.props.direction == 'left') {
      return 'fadeInLeft';
    } else if (this.props.direction == 'right') {
      return 'fadeInRight';
    }
  }

  _startBounce() {
    let xEnd = 0;
    let yEnd = 0;

    if (this.props.isMatchSelect) {
      yEnd = 1;
      xEnd = -4;
    } else if (this.props.direction == 'top') {
      yEnd = -4;
    } else if (this.props.direction == 'bottom') {
      yEnd = -4;
    } else if (this.props.direction == 'left') {
      xEnd = 4;
    } else if (this.props.direction == 'right') {
      xEnd = -4;
    }

    const sequenceArray = [];
    for (let i = 0; i < MILLISECONDS_PER_SECOND; i++) {
      sequenceArray.push(
        Animated.timing(this.state.bounceOffset, {
          delay: 0,
          duration: 600,
          useNativeDriver: true,
          toValue: {
            x: xEnd,
            y: yEnd,
          },
        })
      );
      sequenceArray.push(
        Animated.timing(this.state.bounceOffset, {
          delay: 0,
          duration: 600,
          useNativeDriver: true,
          toValue: {
            x: 0,
            y: 0,
          },
        })
      );
    }

    const bouncing = Animated.sequence(sequenceArray);
    this.setState({bouncing: bouncing});
    setTimeout(() => {
      if (!this.unmounted) {
        bouncing.start();
      }
    }, 300);
  }

  _stopBouncing() {
    this.unmounted = true;
    if (this.state.bouncing) {
      this.state.bouncing.stop();
    }
  }

  _onLayout(e: LayoutChangeEvent) {
    const layout = e.nativeEvent.layout;
    if (layout.width != this.state.size.width || layout.height != this.state.size.height) {
      this.setState({
        size: {
          width: layout.width,
          height: layout.height,
        },
      });
    }
  }

  _shouldBounce(): boolean {
    const canSupportBounce = true; //Sizes.dynamic({sm:false, lg:true, xxlg:false});
    const shouldBounce = (this.props.shouldBounce && canSupportBounce) || false;
    return shouldBounce;
  }

  _shouldHide(layout: RefLayout | undefined): boolean {
    if (!layout) {
      if (this.props.ignoreLayout) {
        return false;
      }
      return true;
    } else if (layout.width <= 0 && layout.height <= 0) {
      return true;
    } else if (layout.rootX <= 0 && layout.rootY <= 0) {
      return true;
    }

    return false;
  }

  render(): React.ReactNode {
    if (!this.state.shouldRender) {
      return false;
    }

    let position = {top: 0, left: 0};
    const layout = this.state.refLayout;

    if (layout && layout.width && layout.height) {
      position = this._getPositionForLayout(
        layout.width,
        layout.height,
        layout.rootX,
        layout.rootY
      );
    } else {
      position = this._getPositionForLayout(0, 0, 0, 0);
    }

    if (this.props.shouldAdjustForHome) {
      const landscapePhoneOffset = Sizes.screenWidth * TAB_BAR_RELATIVE_SIZE;
      position.left = Sizes.select({
        phoneLandscape: position.left - landscapePhoneOffset,
        other: position.left,
      });
    }

    let transform = {};
    if (this._shouldBounce()) {
      transform = {
        transform: this.state.bounceOffset.getTranslateTransform(),
      };
    }

    let pointerImage = false;
    const dynamicStyle = Sizes.isPhone() && Sizes.isLandscape() ? {width: 40, height: 33} : {};
    if (!this.shouldRenderImage) {
      pointerImage = (
        <View
          style={[
            this._getRotationForDirection(),
            this.state.arrowSize,
            {...dynamicStyle, alignSelf: 'center', marginTop: 10},
            SkillzStyle.fullStyles.viewBoxShadowPrimary,
          ]}
          key={JSON.stringify(position)}
        />
      );
    } else if (this.props.shouldNotTheme || Platform.OS == 'android') {
      pointerImage = (
        <SkillzImage
          source={this.state.imageURL}
          resizeMode={'contain'}
          style={[
            this._getRotationForDirection(),
            this.state.arrowSize,
            {...dynamicStyle, alignSelf: 'center', marginTop: 10},
            SkillzStyle.fullStyles.viewBoxShadowPrimary,
          ]}
          key={position as any}
        />
      );
    } else {
      pointerImage = (
        <SkillzImage
          source={this.state.imageURL}
          gradient={[this.state.imageColor]}
          gradientBlendMode={14}
          resizeMode={'contain'}
          style={[
            this._getRotationForDirection(),
            this.state.arrowSize,
            {alignSelf: 'center', marginTop: 10},
            SkillzStyle.fullStyles.viewBoxShadowPrimary,
          ]}
          key={position as any}
        />
      );
    }

    return (
      <Animatable.View
        style={[position, styles.arrow, {opacity: this.state.opacity}]}
        onLayout={this._onLayout.bind(this)}
        animation={this._getAnimationForDirection()}
        easing={Easing.bezier(0.24, 0.42, 0.19, 1)}
        pointerEvents={'none'}
        useNativeDriver={false}
        duration={800}
      >
        <Animated.View style={[transform]} testID={this.props.testID ?? 'highlight_arrow'}>
          {this.props.direction == 'bottom' ? null : this.props.children}
          {pointerImage}
          {this.props.direction == 'top' ? null : this.props.children}
        </Animated.View>
      </Animatable.View>
    );
  }
}

const styles = StyleSheet.create({
  arrow: {
    position: 'absolute',
  },
});
